import React, {
  createContext,
  type ReactNode,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
} from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { Utils } from '@pbt/pbt-ui-components'

import {
  type CompletedRefundTransaction,
  type FailedRefundTransaction,
  type Payment as GraphQlPayment,
  type RefundInvoice,
  useInitiateRefundTransactionMutation,
} from '~/api/graphql/generated/types'
import { PaymentMethod } from '~/constants/addPaymentDialogV2Constants'
import PaymentType from '~/constants/paymentTypes'
import i18n from '~/locales/i18n'
import { refreshBalanceHistory } from '~/store/actions/finance'
import { clearPaymentStoreError, createPayment } from '~/store/actions/payments'
import { getCurrentBusinessId } from '~/store/reducers/auth'
import { getPaymentMethod, getPaymentType } from '~/store/reducers/constants'
import { fetchInvoiceV3 } from '~/store/reducers/invoiceV3'
import {
  getPaymentsError,
  getPaymentsIsLoading,
} from '~/store/reducers/payments'
import type { Payment, UnsavedExtendPayment } from '~/types'
import usePrevious from '~/utils/usePrevious'

import { TransactionFailureReasonId } from '../../../add-payment-dialog-v2/utils/paymentErrorMapping'
import {
  type RefundDialogContextType,
  type RefundDialogStateType,
  RefundFlowState,
} from '../constants/refundDialogV2Constants'
import { initiateLinkedRefundTransaction } from '../utils/initiateLinkedRefundTransactionHelper'
import { initiateRefundTransaction } from '../utils/initiateRefundTransactionHelper'
import {
  calculateRefundableAmount,
  isPartialRefund,
} from '../utils/refundCalculations'
import {
  extractRefundFailureReasonId,
  getRefundTransactionFailureMessage,
} from '../utils/refundErrorMapping'

type RefundDialogAction =
  | { payload: RefundFlowState; type: 'SET_STATE' }
  | { type: 'CANCEL_TRANSACTION' }
  | { type: 'RESET_DIALOG' }
  | {
      payload: {
        message: string
        severity: 'error' | 'warning'
        status: number
      }
      type: 'SET_REFUND_ERROR'
    }
  | { type: 'CLEAR_REFUND_ERROR' }
  | {
      payload: (GraphQlPayment | Payment) | null
      type: 'SET_SELECTED_PAYMENT'
    }
  | {
      payload: number | null
      type: 'SET_REFUND_AMOUNT'
    }
  | { payload: PaymentMethod; type: 'SET_REFUND_METHOD' }
  | { payload: string | null; type: 'SET_REFUND_CARD_TYPE' }
  | { payload: string | null; type: 'SET_REFUND_LAST_FOUR' }
  | {
      payload: { displayName: string; id: string }
      type: 'SET_SELECTED_PAYMENT_TERMINAL'
    }
  | { payload: string | null; type: 'SET_TRANSACTION_ID' }
  | { payload: boolean; type: 'SET_UNLINKED' }
  | { payload: boolean; type: 'SET_LINKED' }
  | { payload: boolean; type: 'SET_LINKED_REFUND_LISTENING' }
  | { payload: number; type: 'SET_PARTIAL_REFUND' }
  | { type: 'CLEAR_PARTIAL_REFUND' }
  | { payload: { totalRefunded: number }; type: 'RESET_FOR_PARTIAL_REFUND' }

type ActionHandlers = Record<
  RefundDialogAction['type'],
  (
    state: RefundDialogStateType,
    action: RefundDialogAction,
  ) => RefundDialogStateType
>

interface RefundDialogProviderProps {
  children: ReactNode
  refundInvoice: RefundInvoice
}

const createInitialDialogState = (): RefundDialogStateType => ({
  currentState: RefundFlowState.ISSUE_REFUND,
  hasPartialRefund: false,
  isLinkedRefundListening: false,
  linked: false,
  partialRefundAmount: 0,
  refundAmount: 0,
  refundCardType: null,
  refundError: null,
  refundLastFour: null,
  refundMethod: PaymentMethod.CREDIT_DEBIT,
  selectedPayment: null,
  selectedPaymentTerminal: null,
  selectedPaymentTerminalDisplayName: null,
  transactionId: null,
  unlinked: false,
})

const actionHandlers: ActionHandlers = {
  SET_STATE: (state, action) => ({
    ...state,
    currentState: (action as { payload: RefundFlowState }).payload,
  }),
  CANCEL_TRANSACTION: (state) => ({
    ...state,
    currentState: RefundFlowState.ISSUE_REFUND,
  }),
  RESET_DIALOG: (state) => ({
    ...state,
    ...createInitialDialogState(),
  }),
  SET_REFUND_ERROR: (state, action) => ({
    ...state,
    refundError: (
      action as {
        payload: {
          message: string
          severity: 'error' | 'warning'
          status: number
        }
      }
    ).payload,
  }),
  CLEAR_REFUND_ERROR: (state) => ({
    ...state,
    refundError: null,
  }),
  SET_SELECTED_PAYMENT: (state, action) => ({
    ...state,
    selectedPayment: (
      action as {
        payload: (GraphQlPayment | Payment) | null
      }
    ).payload,
  }),
  SET_REFUND_AMOUNT: (state, action) => ({
    ...state,
    refundAmount: (
      action as {
        payload: number
      }
    ).payload,
  }),
  SET_REFUND_METHOD: (state, action) => ({
    ...state,
    refundMethod: (action as { payload: PaymentMethod }).payload,
  }),
  SET_REFUND_CARD_TYPE: (state, action) => ({
    ...state,
    refundCardType: (action as { payload: string | null }).payload,
  }),
  SET_REFUND_LAST_FOUR: (state, action) => ({
    ...state,
    refundLastFour: (action as { payload: string | null }).payload,
  }),
  SET_SELECTED_PAYMENT_TERMINAL: (state, action) => ({
    ...state,
    selectedPaymentTerminal: (
      action as { payload: { displayName: string; id: string } }
    ).payload.id,
    selectedPaymentTerminalDisplayName: (
      action as { payload: { displayName: string; id: string } }
    ).payload.displayName,
  }),
  SET_TRANSACTION_ID: (state, action) => ({
    ...state,
    transactionId: (action as { payload: string | null }).payload,
  }),
  SET_UNLINKED: (state, action) => ({
    ...state,
    unlinked: (action as { payload: boolean }).payload,
  }),
  SET_LINKED: (state, action) => ({
    ...state,
    linked: (action as { payload: boolean }).payload,
  }),
  SET_LINKED_REFUND_LISTENING: (state, action) => ({
    ...state,
    isLinkedRefundListening: (action as { payload: boolean }).payload,
  }),
  SET_PARTIAL_REFUND: (state, action) => ({
    ...state,
    hasPartialRefund: true,
    partialRefundAmount: (action as { payload: number }).payload,
  }),
  CLEAR_PARTIAL_REFUND: (state) => ({
    ...state,
    hasPartialRefund: false,
    partialRefundAmount: 0,
  }),
  RESET_FOR_PARTIAL_REFUND: (state, action) => {
    const { totalRefunded } = (
      action as {
        payload: { totalRefunded: number }
      }
    ).payload
    return {
      ...state,
      currentState: RefundFlowState.ISSUE_REFUND,
      hasPartialRefund: true,
      isLinkedRefundListening: false,
      linked: false,
      partialRefundAmount: totalRefunded,
      refundAmount: 0,
      refundCardType: null,
      refundMethod: PaymentMethod.CREDIT_DEBIT,
      refundLastFour: null,
      selectedPayment: null,
      selectedPaymentTerminal: null,
      selectedPaymentTerminalDisplayName: null,
      transactionId: null,
      unlinked: false,
    }
  },
}

const refundDialogReducer = (
  state: RefundDialogStateType,
  action: RefundDialogAction,
): RefundDialogStateType => {
  // Handle case where state is undefined (can happen in test environments)
  if (!state) {
    return createInitialDialogState()
  }
  const handler = actionHandlers[action.type]
  return handler ? handler(state, action) : state
}

const RefundDialogContext = createContext<RefundDialogContextType | undefined>(
  undefined,
)

export const RefundDialogProvider = ({
  children,
  refundInvoice,
}: RefundDialogProviderProps) => {
  const [dialogState, dispatch] = useReducer(
    refundDialogReducer,
    createInitialDialogState(),
  )
  const [isLoading, setIsLoading] = useState(false)
  const [pendingRefundData, setPendingRefundData] = useState<{
    refundAmount: number
    selectedPayment: GraphQlPayment | Payment
  } | null>(null)
  const businessId = useSelector(getCurrentBusinessId) || ''
  const PaymentTypeConstant = useSelector(getPaymentType)
  const PaymentMethodConstant = useSelector(getPaymentMethod)
  const reduxDispatch = useDispatch()
  const { t } = useTranslation(['Dialogs'])

  // Redux state selectors for payment operations
  const paymentError = useSelector(getPaymentsError)
  const paymentLoading = useSelector(getPaymentsIsLoading)
  const wasPaymentLoading = usePrevious(paymentLoading)

  const [initiateRefundTransactionMutation] =
    useInitiateRefundTransactionMutation()

  const PaymentMethodsToIdsMap = useMemo(
    () => ({
      [PaymentMethod.CASH]: `${Utils.findConstantIdByName('Cash', PaymentMethodConstant)}`,
      [PaymentMethod.CREDIT_DEBIT]: `${Utils.findConstantIdByName('Credit card', PaymentMethodConstant)}`,
      [PaymentMethod.CHECK]: `${Utils.findConstantIdByName('Check', PaymentMethodConstant)}`,
      [PaymentMethod.CARECREDIT]: `${Utils.findConstantIdByName('CareCredit', PaymentMethodConstant)}`,
    }),
    [PaymentMethodConstant],
  )

  // Track previous refundable amount to detect refunds made in other tabs
  const previousRefundableAmountRef = useRef(
    calculateRefundableAmount(refundInvoice),
  )

  const handlePaymentSuccessState = (
    currentPendingRefundData: {
      refundAmount: number
      selectedPayment: GraphQlPayment | Payment
    },
    currentRefundInvoice: RefundInvoice,
  ) => {
    // TODO: Determine difference between amount and paidAmountNoFee for 3P refunds
    const partialRefund = isPartialRefund(
      currentPendingRefundData.refundAmount,
      currentRefundInvoice,
    )

    if (partialRefund) {
      // This is a partial refund - store the current refund amount
      dispatch({
        type: 'RESET_FOR_PARTIAL_REFUND',
        payload: {
          totalRefunded: currentPendingRefundData.refundAmount,
        },
      })
    } else {
      // This is a full refund
      dispatch({ type: 'CLEAR_PARTIAL_REFUND' })
      dispatch({ type: 'SET_STATE', payload: RefundFlowState.REFUND_SUCCESS })
    }
    setIsLoading(false)
    setPendingRefundData(null)
  }

  // Listen for Refund success/failure states
  // Only react to errors/success if we have pending refund data (i.e., we initiated the action)
  useEffect(() => {
    const currentRefundableAmount = calculateRefundableAmount(refundInvoice)
    const previousRefundableAmount = previousRefundableAmountRef.current

    if (paymentError && pendingRefundData) {
      // For manual refund errors from Redux, use generic connectivity message
      // as we don't have failure reason ID information from the API error
      const errorMessage = i18n.t(
        'Dialogs:REFUND_DIALOG_V2.ERRORS.TRANSACTION_CONNECTIVITY',
      )

      dispatch({
        type: 'SET_REFUND_ERROR',
        payload: {
          message: errorMessage,
          severity: 'error',
          status: 500,
        },
      })

      setIsLoading(false)
      setPendingRefundData(null)
    } else if (
      !paymentLoading &&
      wasPaymentLoading &&
      !paymentError &&
      pendingRefundData
    ) {
      // Refund completed successfully in this tab - move to success state
      handlePaymentSuccessState(pendingRefundData, refundInvoice)
    } else if (
      // Detect partial refund made in another tab or external source
      previousRefundableAmount !== undefined &&
      currentRefundableAmount < previousRefundableAmount &&
      currentRefundableAmount > 0 &&
      !paymentLoading &&
      !pendingRefundData &&
      dialogState.currentState === RefundFlowState.ISSUE_REFUND
    ) {
      dispatch({
        type: 'RESET_FOR_PARTIAL_REFUND',
        payload: {
          totalRefunded: previousRefundableAmount - currentRefundableAmount,
        },
      })
    }

    // Update ref for next comparison
    previousRefundableAmountRef.current = currentRefundableAmount
  }, [
    paymentError,
    paymentLoading,
    wasPaymentLoading,
    pendingRefundData,
    dialogState.currentState,
    refundInvoice,
    dispatch,
  ])
  const actions = useMemo(
    () => ({
      cancelTransaction: () => {
        setIsLoading(false)
        setPendingRefundData(null)
        dispatch({ type: 'CANCEL_TRANSACTION' })
      },
      resetDialog: () => {
        setIsLoading(false)
        setPendingRefundData(null)
        dispatch({ type: 'RESET_DIALOG' })
      },
      setRefundError: (refundError: {
        message: string
        severity: 'error' | 'warning'
        status: number
      }) => dispatch({ type: 'SET_REFUND_ERROR', payload: refundError }),
      clearRefundError: () => {
        dispatch({ type: 'CLEAR_REFUND_ERROR' })
        reduxDispatch(clearPaymentStoreError())
      },
      setRefundAmount: (amount: number) =>
        dispatch({ type: 'SET_REFUND_AMOUNT', payload: amount }),
      setRefundMethod: (method: PaymentMethod) =>
        dispatch({ type: 'SET_REFUND_METHOD', payload: method }),
      setSelectedPaymentTerminal: (terminalId: string, displayName: string) =>
        dispatch({
          type: 'SET_SELECTED_PAYMENT_TERMINAL',
          payload: { id: terminalId, displayName },
        }),
      setTransactionId: (transactionId: string | null) =>
        dispatch({
          type: 'SET_TRANSACTION_ID',
          payload: transactionId,
        }),
      setSelectedPayment: (payment: GraphQlPayment | Payment | null) => {
        dispatch({ type: 'SET_SELECTED_PAYMENT', payload: payment })
        if (!payment) {
          dispatch({ type: 'SET_UNLINKED', payload: false })
          dispatch({ type: 'SET_LINKED', payload: false })
          dispatch({ type: 'SET_LINKED_REFUND_LISTENING', payload: false })
          return
        }

        const hasCardDetails = Boolean(payment.cardType && payment.lastFour)
        const graphqlPayment = payment as GraphQlPayment

        const hasPosTxIdOrGoTxId = Boolean(
          graphqlPayment.posTxId || graphqlPayment.goTxId,
        )

        if (hasCardDetails) {
          dispatch({
            type: 'SET_REFUND_METHOD',
            payload: PaymentMethod.CREDIT_DEBIT,
          })
          dispatch({
            type: 'SET_SELECTED_PAYMENT_TERMINAL',
            payload: { id: '', displayName: '' },
          })

          if (hasPosTxIdOrGoTxId) {
            // Unlinked refund flow: payment has posTxId or goTxId
            dispatch({ type: 'SET_UNLINKED', payload: true })
            dispatch({ type: 'SET_LINKED', payload: false })
          } else {
            // Linked refund flow: card payment without posTxId/goTxId
            dispatch({ type: 'SET_LINKED', payload: true })
            dispatch({ type: 'SET_UNLINKED', payload: false })
          }
        } else {
          // Non-card payment flow: Manual refund flow
          dispatch({ type: 'SET_UNLINKED', payload: false })
          dispatch({ type: 'SET_LINKED', payload: false })
        }
        dispatch({ type: 'SET_LINKED_REFUND_LISTENING', payload: false })
      },
      setState: (state: RefundFlowState) =>
        dispatch({ type: 'SET_STATE', payload: state }),
      submitManualRefund: (currentDialogState: RefundDialogStateType) => {
        if (!currentDialogState.selectedPayment) {
          dispatch({
            type: 'SET_REFUND_ERROR',
            payload: {
              message: t('Dialogs:REFUND_DIALOG_V2.PLEASE_SELECT_PAYMENT'),
              severity: 'error',
              status: 400,
            },
          })
          return
        }

        setIsLoading(true)
        dispatch({ type: 'CLEAR_REFUND_ERROR' })
        reduxDispatch(clearPaymentStoreError())

        const { selectedPayment } = currentDialogState
        const paymentMethodId = selectedPayment.method?.id
        const clientId = refundInvoice.client?.id

        // Track pending refund to ensure we only react to our own actions
        setPendingRefundData({
          refundAmount: currentDialogState.refundAmount,
          selectedPayment,
        })
        const refundPayment: Partial<UnsavedExtendPayment> = {
          amount: currentDialogState.refundAmount,
          originPaymentId: selectedPayment.id,
          paidByPersonId: clientId,
          paymentMethodId: currentDialogState.unlinked
            ? PaymentMethodsToIdsMap[currentDialogState.refundMethod]
            : paymentMethodId,
          paymentTypeId: Utils.findConstantIdByName(
            PaymentType.REVERSE_CHARGE,
            PaymentTypeConstant,
          ),
          refundInvoiceId: refundInvoice.id,
          reversed: false,
        }

        reduxDispatch(createPayment(clientId, refundPayment))
      },
      submitUnlinkedRefund: async (
        currentDialogState: RefundDialogStateType,
      ) => {
        if (!currentDialogState.selectedPayment) {
          dispatch({
            type: 'SET_REFUND_ERROR',
            payload: {
              message: t('Dialogs:REFUND_DIALOG_V2.PLEASE_SELECT_PAYMENT'),
              severity: 'error',
              status: 400,
            },
          })
          return
        }
        setIsLoading(true)
        dispatch({ type: 'CLEAR_REFUND_ERROR' })
        reduxDispatch(clearPaymentStoreError())

        await initiateRefundTransaction({
          dispatch,
          initiateRefundTransactionMutation,
          refundAmount: currentDialogState.refundAmount,
          refundInvoiceId: refundInvoice.id,
          selectedPaymentId: currentDialogState.selectedPayment.id,
          selectedPaymentTerminal: currentDialogState.selectedPaymentTerminal,
          setIsLoading,
        })
      },
      submitLinkedRefund: async (currentDialogState: RefundDialogStateType) => {
        if (!currentDialogState.selectedPayment) {
          dispatch({
            type: 'SET_REFUND_ERROR',
            payload: {
              message: t('Dialogs:REFUND_DIALOG_V2.PLEASE_SELECT_PAYMENT'),
              severity: 'error',
              status: 400,
            },
          })
          return
        }
        setIsLoading(true)
        dispatch({ type: 'CLEAR_REFUND_ERROR' })
        reduxDispatch(clearPaymentStoreError())

        await initiateLinkedRefundTransaction({
          dispatch,
          initiateRefundTransactionMutation,
          refundAmount: currentDialogState.refundAmount,
          refundInvoiceId: refundInvoice.id,
          selectedPaymentId: currentDialogState.selectedPayment.id,
          setIsLoading,
          t,
        })
      },
      handleRefundTransactionSuccess: (
        currentRefundState: RefundDialogStateType,
        transaction: CompletedRefundTransaction,
      ) => {
        if (!currentRefundState.selectedPayment) {
          dispatch({
            type: 'SET_REFUND_ERROR',
            payload: {
              message: t('Dialogs:REFUND_DIALOG_V2.PLEASE_SELECT_PAYMENT'),
              severity: 'error',
              status: 400,
            },
          })
          return
        }
        const { selectedPayment } = currentRefundState
        const refundAmount = transaction.amount

        // For unlinked refunds, store the card details from the transaction since it will be on a new card (potentially)
        if (transaction.cardDetails && currentRefundState.unlinked) {
          dispatch({
            type: 'SET_REFUND_CARD_TYPE',
            payload: transaction.cardDetails.type?.name || null,
          })
          dispatch({
            type: 'SET_REFUND_LAST_FOUR',
            payload: transaction.cardDetails.lastFour || null,
          })
        }

        // Move to success state
        dispatch({
          type: 'SET_STATE',
          payload: RefundFlowState.REFUND_SUCCESS,
        })
        handlePaymentSuccessState(
          { refundAmount, selectedPayment },
          refundInvoice,
        )
        // Refresh the invoice after a successful refund
        reduxDispatch(fetchInvoiceV3({ id: refundInvoice.id }))
        if (refundInvoice.client?.id) {
          reduxDispatch(refreshBalanceHistory(refundInvoice.client.id))
        }
      },
      handleRefundTransactionFailure: (
        currentDialogState: RefundDialogStateType,
        transaction: FailedRefundTransaction,
      ) => {
        dispatch({ type: 'CANCEL_TRANSACTION' })
        dispatch({
          type: 'SET_REFUND_ERROR',
          payload: {
            message: getRefundTransactionFailureMessage(
              transaction,
              currentDialogState.linked,
            ),
            severity: 'error',
            status: 500,
          },
        })

        // If linked refund failed, fall back to unlinked flow unless failure reason is UNKNOWN
        if (
          currentDialogState.linked &&
          extractRefundFailureReasonId(transaction) !==
            TransactionFailureReasonId.UNKNOWN
        ) {
          dispatch({ type: 'SET_LINKED', payload: false })
          dispatch({ type: 'SET_UNLINKED', payload: true })
          dispatch({ type: 'SET_LINKED_REFUND_LISTENING', payload: false })
          dispatch({ type: 'SET_TRANSACTION_ID', payload: null })
        }
        setIsLoading(false)
      },
      handleRefundTransactionHardFailure: (
        currentDialogState: RefundDialogStateType,
      ) => {
        dispatch({ type: 'CANCEL_TRANSACTION' })
        dispatch({
          type: 'SET_REFUND_ERROR',
          payload: {
            message: i18n.t(
              'Dialogs:REFUND_DIALOG_V2.ERRORS.TRANSACTION_CONNECTIVITY',
            ),
            severity: 'error',
            status: 500,
          },
        })

        // If linked refund failed, fall back to unlinked flow
        if (currentDialogState.linked) {
          dispatch({ type: 'SET_LINKED', payload: false })
          dispatch({ type: 'SET_UNLINKED', payload: true })
          dispatch({ type: 'SET_LINKED_REFUND_LISTENING', payload: false })
          dispatch({ type: 'SET_TRANSACTION_ID', payload: null })
        }
        setIsLoading(false)
      },
    }),
    [
      dispatch,
      reduxDispatch,
      setIsLoading,
      dialogState.selectedPayment,
      dialogState.refundAmount,
      refundInvoice,
      businessId,
      PaymentTypeConstant,
      t,
      initiateRefundTransactionMutation,
    ],
  )

  const contextValue = useMemo(
    () => ({
      dialogState,
      isLoading,
      refundInvoice,
      actions,
    }),
    [dialogState, isLoading, refundInvoice, actions],
  )
  return (
    <RefundDialogContext.Provider value={contextValue}>
      {children}
    </RefundDialogContext.Provider>
  )
}

export const useRefundDialog = (): RefundDialogContextType => {
  const context = useContext(RefundDialogContext)
  if (!context) {
    throw new Error(
      'useRefundDialog must be used within a RefundDialogProvider',
    )
  }
  return context
}
